import { createContext, FC, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { LayerStatesContextValue, LayerStatesProviderProps } from './types'
import { useLayers } from 'hooks/queries/layers/useLayers'
import { EditorState } from 'prosemirror-state'
import { Layer, RootState } from 'types'
import { useSelector } from 'react-redux'
import isEqual from 'lodash/isEqual'
import { createStateFromDoc } from '../StateWithPresenceProvider/utils'
import { NOOP, ourUuid } from 'utils'
import { useQueryParams } from 'hooks/useQueryParams'
import { RealTimeReceivedPayload } from '../RealtimeProvider/utils'
import { Step } from 'prosemirror-transform'
import { getVersion, receiveTransaction } from 'prosemirror-collab'
import { useQueryClient } from 'react-query'
import { TrackedChanges } from 'stores/TrackedChanges'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { DefaultDisposer } from 'stores/TrackedChanges/DefaultDisposer'
import { useAuth } from 'hooks/UseAuth'
import { useDocStore } from 'stores/DocStore'
import { useAuthStore } from 'stores/AuthStore'
import { useModals } from 'almanac-ui/Modal'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'

const LayerStatesContext = createContext<LayerStatesContextValue>({
  updateLayerStates: NOOP,
  setLayerState: NOOP,
  getLayerState: () => null,
  clientUuid: '',
  currentLayer: null,
  layers: [],
  isLoading: false,
  currentLayerState: null,
})

const LayerStatesContextProvider = LayerStatesContext.Provider

export const LayerStatesProvider: FC<React.PropsWithChildren<LayerStatesProviderProps>> = observer(
  ({ children, article, readOnly = false, inCommentMode }) => {
    const featureFlags = useRootStore().featureFlags
    const auth = useAuth()
    const queryClient = useQueryClient()
    const features = [...featureFlags.flags()].filter(([flag, enabled]) => enabled).map(([flag]) => flag)
    const { layer: currentLayerId } = useQueryParams()
    const [currentLayer, setCurrentLayer] = useState<Layer | null>(null)
    const [currentLayerState, setCurrentLayerState] = useState<EditorState | null>(null)
    const { data, isLoading: layersLoading } = useLayers({ articleKey: article.id })
    const { isRegistered } = useSelector((state: RootState) => ({ isRegistered: !!state.auth }), isEqual)
    const [clientUuid] = useState(ourUuid())
    const layerStates = useRef<{ [key: string]: EditorState }>({})
    const { isMobile } = useResponsiveDesign()
    const docStore = useDocStore()
    const authStore = useAuthStore()
    const stackManager = useModals()

    const isLoading = featureFlags.isLoading || layersLoading

    const updateLayerStates = useCallback(
      ({ layer_id: layerId, client_uuids, steps: stepsJson, versions }: RealTimeReceivedPayload) => {
        const layerState = layerStates.current[layerId]

        if (!layerState) {
          return
        }

        const source = client_uuids[0] !== clientUuid ? 'remote' : 'local'

        // Filter out old steps before the initially loaded version
        const filteredStepsJson = stepsJson.filter((_, i) => versions[i] >= getVersion(layerState))

        if (!filteredStepsJson.length) return

        const steps = filteredStepsJson.map((s) => Step.fromJSON(layerState.schema, s))

        const tr = receiveTransaction(layerState, steps, client_uuids, { mapSelectionBackward: true })
        tr.setMeta('realTime', true)
        tr.setMeta('source', source)
        layerStates.current[layerId] = layerState.apply(tr)
      },
      [clientUuid],
    )

    const getLayerState = useCallback((layerId: string) => {
      return layerStates.current[layerId]
    }, [])

    const setLayerState = useCallback(
      (layerId: string, newState: EditorState) => {
        layerStates.current[layerId] = newState
        const layer = data?.layers.find((layer) => layer.id === layerId)
        const isMainLayer = !layer?.parent_layer
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const isCurrentLayer = (!currentLayerId && isMainLayer) || layer!.id === currentLayerId

        if (isCurrentLayer) {
          setCurrentLayerState(newState)
        }
      },
      [currentLayerId, data],
    )

    useEffect(() => {
      if (isLoading) {
        return
      }

      setCurrentLayer(
        data?.layers.find((layer) => (!currentLayerId && !layer.parent_layer) || layer.id === currentLayerId) || null,
      )
    }, [currentLayerId, data, isLoading])

    useEffect(() => {
      if (data && !isLoading) {
        const disabledPlugins: string[] = []
        if (!isRegistered) disabledPlugins.push('MentionPromptPlugin')
        const { layers } = data
        layers.forEach((layer: Layer) => {
          const isMainLayer = !layer.parent_layer
          const isCurrentLayer = (!currentLayerId && isMainLayer) || layer.id === currentLayerId
          if (!layerStates.current[layer.id]) {
            layerStates.current[layer.id] = createStateFromDoc({
              auth,
              authStore: authStore,
              queryClient: queryClient,
              article: article,
              layer: layer,
              clientUuid: clientUuid,
              commentOnly: !!inCommentMode,
              readOnly: readOnly,
              isMobile,
              disabledPlugins: disabledPlugins,
              features: features,
              stores: {
                trackChanges: new TrackedChanges({
                  disposer: new DefaultDisposer(queryClient, layer),
                  trackingChanges: !isMainLayer,
                  docStore,
                }),
                review: undefined,
                docStore,
              },
              stackManager,
            })
          }
          if (isCurrentLayer) {
            setCurrentLayer(layer)
            setCurrentLayerState(layerStates.current[layer.id])
          }
        })
      }
    }, [authStore, data, isLoading, isMobile, inCommentMode, readOnly]) // eslint-disable-line react-hooks/exhaustive-deps

    const value = {
      updateLayerStates,
      setLayerState,
      clientUuid,
      currentLayer,
      getLayerState,
      layers: data?.layers || [],
      isLoading,
      currentLayerState,
    }

    return <LayerStatesContextProvider value={value}>{children}</LayerStatesContextProvider>
  },
)

export const useLayerStates = () => useContext(LayerStatesContext)
